document.documentElement.classList.remove('no-js');

(() => {
  const doc = document;
  const root = doc.documentElement;
  const body = doc.body;
  const siteHeader = doc.querySelector('[data-site-header]') || doc.querySelector('.site-header');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktopMenu = window.matchMedia('(min-width: 761px)');
  const mobileMenu = window.matchMedia('(max-width: 760px)');

  const setHeaderBottom = () => {
    if (!siteHeader) return;
    const rect = siteHeader.getBoundingClientRect();
    root.style.setProperty('--header-bottom', `${Math.max(0, Math.round(rect.bottom))}px`);
    root.style.setProperty('--site-header-height', `${Math.max(0, Math.round(rect.height))}px`);
  };

  setHeaderBottom();
  window.addEventListener('resize', setHeaderBottom, { passive: true });
  window.addEventListener('scroll', setHeaderBottom, { passive: true });
  window.addEventListener('orientationchange', () => window.setTimeout(setHeaderBottom, 250), { passive: true });

  // Quantity safety for product, cart, and quick-order fields.
  doc.querySelectorAll('input[name="quantity"]').forEach((input) => {
    input.addEventListener('change', () => {
      const min = Number(input.getAttribute('min') || 1);
      const value = Number(input.value || min);
      if (Number.isNaN(value) || value < min) input.value = min;
    });
  });

  // Collection filters: closed by default on phones, visible on larger screens.
  const filters = doc.querySelector('[data-collection-filters]');
  const filterToggle = doc.querySelector('[data-filter-toggle]');
  const syncFilters = () => {
    if (!filters) return;
    if (window.matchMedia('(max-width: 640px)').matches) {
      if (!filters.dataset.userToggled) filters.hidden = true;
    } else {
      filters.hidden = false;
      delete filters.dataset.userToggled;
    }
    if (filterToggle) filterToggle.setAttribute('aria-expanded', String(!filters.hidden));
  };
  if (filterToggle && filters) {
    filterToggle.addEventListener('click', () => {
      filters.dataset.userToggled = 'true';
      filters.hidden = !filters.hidden;
      filterToggle.setAttribute('aria-expanded', String(!filters.hidden));
    });
  }
  syncFilters();
  window.addEventListener('resize', syncFilters, { passive: true });

  // Fully functional main mega menu: click, hover on desktop, outside click, keyboard, and resize cleanup.
  const megaItems = Array.from(doc.querySelectorAll('[data-mega-item], .mega-nav__item')).filter((item) => item.querySelector('[data-mega-trigger], .mega-nav__trigger'));

  const closeMega = (item) => {
    if (!item) return;
    const trigger = item.querySelector('[data-mega-trigger], .mega-nav__trigger');
    const panel = item.querySelector('[data-mega-panel], .mega-nav__panel');
    item.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  };

  const closeAllMega = (except = null) => {
    megaItems.forEach((item) => {
      if (item !== except) closeMega(item);
    });
  };

  const openMega = (item) => {
    if (!item) return;
    const trigger = item.querySelector('[data-mega-trigger], .mega-nav__trigger');
    const panel = item.querySelector('[data-mega-panel], .mega-nav__panel');
    if (!trigger || !panel) return;
    closeAllMega(item);
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    setHeaderBottom();
  };

  megaItems.forEach((item) => {
    const trigger = item.querySelector('[data-mega-trigger], .mega-nav__trigger');
    const panel = item.querySelector('[data-mega-panel], .mega-nav__panel');
    if (!trigger || !panel) return;

    closeMega(item);

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      item.classList.contains('is-open') ? closeMega(item) : openMega(item);
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openMega(item);
        const firstLink = panel.querySelector('a, button');
        if (firstLink) firstLink.focus({ preventScroll: true });
      }
    });

    item.addEventListener('mouseenter', () => {
      if (desktopMenu.matches && finePointer.matches) openMega(item);
    });
    item.addEventListener('mouseleave', () => {
      if (desktopMenu.matches && finePointer.matches) closeMega(item);
    });

    panel.addEventListener('click', (event) => event.stopPropagation());
  });

  doc.addEventListener('click', (event) => {
    if (!event.target.closest('.mega-nav__item')) closeAllMega();
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllMega();
      const openTrigger = doc.querySelector('.mega-nav__trigger[aria-expanded="true"]');
      if (openTrigger) openTrigger.focus({ preventScroll: true });
    }
  });

  window.addEventListener('resize', () => {
    closeAllMega();
    setHeaderBottom();
  }, { passive: true });

  // Active states for main nav and bottom mobile bar.
  const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
  doc.querySelectorAll('.mega-nav__link, .mobile-action-bar__item').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return;
    try {
      const linkPath = new URL(href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      if (linkPath === currentPath) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      }
    } catch (error) { /* Ignore invalid URLs. */ }
  });

  // Hide mobile bottom bar while typing so it never covers form fields.
  const editableSelector = 'input, textarea, select';
  doc.addEventListener('focusin', (event) => {
    if (event.target && event.target.matches && event.target.matches(editableSelector)) {
      root.classList.add('is-editing-field');
    }
  });
  doc.addEventListener('focusout', () => {
    window.setTimeout(() => {
      if (!doc.activeElement || !doc.activeElement.matches(editableSelector)) {
        root.classList.remove('is-editing-field');
      }
    }, 80);
  });

  // Reveal animations.
  const revealSelector = [
    '.section-header', '.promo-card', '.category-card', '.product-card', '.value-card', '.solution-card',
    '.testimonial-card', '.logo-card', '.organic-trust-item', '.process-3d__step', '.newsletter-band__inner',
    '.collection-hero', '.form-panel', '.cart-item', '.quick-order-row', '.product-media', '.product-info'
  ].join(',');

  doc.querySelectorAll(revealSelector).forEach((item) => {
    if (!item.hasAttribute('data-reveal')) item.setAttribute('data-reveal', '');
  });

  const revealItems = doc.querySelectorAll('[data-reveal]');
  if (reducedMotion.matches) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  } else if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  // Lightweight parallax and card tilt.
  const parallaxSections = Array.from(doc.querySelectorAll('[data-parallax-section]'));
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    if (reducedMotion.matches) return;
    parallaxSections.forEach((section) => {
      const layer = section.querySelector('[data-parallax-layer]');
      if (!layer) return;
      const rect = section.getBoundingClientRect();
      const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      layer.style.setProperty('--parallax-y', `${(clamped - 0.5) * 36}px`);
    });
  };
  const requestParallax = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateParallax);
  };
  updateParallax();
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });

  if (finePointer.matches && !reducedMotion.matches) {
    doc.querySelectorAll('.promo-card, .category-card, .product-card, .value-card, .solution-card, .testimonial-card, .process-3d__step').forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 6;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * -6;
        card.style.setProperty('--tilt-x', `${y.toFixed(2)}deg`);
        card.style.setProperty('--tilt-y', `${x.toFixed(2)}deg`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.removeProperty('--tilt-x');
        card.style.removeProperty('--tilt-y');
      });
    });
  }

  // Ultra-premium intro popup with a single directors CTA and one browser-safe sound control.
  const gate = doc.querySelector('[data-intro-gate]');
  if (!gate) return;

  const video = gate.querySelector('[data-intro-video]');
  const progress = gate.querySelector('[data-intro-progress]');
  const closeTriggers = gate.querySelectorAll('[data-intro-skip], [data-intro-enter], [data-intro-close]');
  const directorLinks = gate.querySelectorAll('[data-intro-directors]');
  const soundButtons = Array.from(gate.querySelectorAll('[data-intro-sound]'));
  const soundLabels = Array.from(gate.querySelectorAll('[data-intro-sound-label]'));
  const frequency = gate.dataset.introFrequency || 'session';
  const autoplay = gate.dataset.introAutoplay === 'true';
  const storageKey = 'vascoraIntroSeen:v2.13-welcome-visible';
  const focusableSelector = 'a[href], button:not([disabled]), video[controls], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  let previousFocus = null;

  const storage = {
    get() {
      try {
        if (frequency === 'session') return window.sessionStorage.getItem(storageKey);
        if (frequency === 'daily') return window.localStorage.getItem(storageKey);
      } catch (error) { return null; }
      return null;
    },
    set() {
      try {
        if (frequency === 'session') window.sessionStorage.setItem(storageKey, 'seen');
        if (frequency === 'daily') window.localStorage.setItem(storageKey, String(Date.now()));
      } catch (error) { /* Storage can be unavailable in private modes. */ }
    },
    isDailyFresh(value) {
      if (!value || frequency !== 'daily') return false;
      return Date.now() - Number(value) < 24 * 60 * 60 * 1000;
    }
  };

  const seenValue = storage.get();
  if ((frequency === 'session' && seenValue) || (frequency === 'daily' && storage.isDailyFresh(seenValue))) return;

  const updateSoundUI = (hasSound) => {
    const label = hasSound ? 'Mute' : (gate.dataset.soundLabel || 'Sound On');
    gate.setAttribute('data-sound-state', hasSound ? 'on' : 'muted');
    soundButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(hasSound));
      button.setAttribute('aria-label', hasSound ? 'Turn intro video sound off' : 'Turn intro video sound on');
    });
    soundLabels.forEach((el) => { el.textContent = label; });
  };

  const focusFirst = () => {
    const focusable = Array.from(gate.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
    const target = gate.querySelector('[data-intro-enter]') || gate.querySelector('[data-intro-directors]') || gate.querySelector('[data-intro-skip]') || gate.querySelector('[data-intro-sound]') || focusable[0];
    if (target && target.focus) target.focus({ preventScroll: true });
  };

  const openGate = () => {
    previousFocus = doc.activeElement;
    gate.hidden = false;
    body.classList.add('is-intro-active');
    focusFirst();

    if (video) {
      video.controls = false;
      video.volume = 1;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      updateSoundUI(false);

      if (autoplay && !reducedMotion.matches) {
        video.play().catch(() => {
          updateSoundUI(false);
        });
      } else {
        video.pause();
      }
    }
  };

  const closeGate = () => {
    storage.set();
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    gate.hidden = true;
    body.classList.remove('is-intro-active');
    if (previousFocus && previousFocus.focus) {
      previousFocus.focus({ preventScroll: true });
    } else {
      const main = doc.getElementById('MainContent');
      if (main && main.focus) main.focus({ preventScroll: true });
    }
  };

  const playWithSound = async () => {
    if (!video) return;
    try {
      video.volume = 1;
      video.defaultMuted = false;
      video.muted = false;
      video.removeAttribute('muted');
      if (video.paused) await video.play();
      updateSoundUI(true);
    } catch (error) {
      // Browser blocked playback. Show native controls as a reliable fallback.
      video.controls = true;
      updateSoundUI(false);
    }
  };

  const muteSound = () => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    updateSoundUI(false);
  };

  closeTriggers.forEach((button) => button.addEventListener('click', closeGate));
  directorLinks.forEach((link) => {
    link.addEventListener('click', () => storage.set());
  });

  soundButtons.forEach((button) => {
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!video) return;
      if (video.muted) {
        await playWithSound();
      } else {
        muteSound();
      }
    });
  });

  if (video) {
    video.addEventListener('volumechange', () => updateSoundUI(!video.muted && video.volume > 0));
  }

  if (video && progress) {
    video.addEventListener('timeupdate', () => {
      const duration = video.duration || 0;
      const percent = duration > 0 ? Math.min(100, (video.currentTime / duration) * 100) : 0;
      progress.style.width = `${percent}%`;
    });
    video.addEventListener('ended', () => {
      progress.style.width = '100%';
      window.setTimeout(closeGate, 700);
    });
  }

  gate.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeGate();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(gate.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.setTimeout(openGate, 250);
})();

// Vascora secure contact form: lightweight client-side spam screening layered with Shopify hCaptcha/spam filters.
(() => {
  const forms = Array.from(document.querySelectorAll('form.secure-contact-form'));
  if (!forms.length) return;

  const MIN_SECONDS = 3.5;
  const RATE_LIMIT_SECONDS = 25;
  const urlPattern = /(https?:\/\/|www\.|\.com\b|\.net\b|\.ru\b|\.xyz\b)/gi;
  const spamSignals = [
    'seo services', 'guest post', 'backlinks', 'casino', 'viagra', 'forex trading',
    'crypto investment', 'telegram channel', 'website traffic', 'rank higher on google'
  ];

  const showAlert = (form, message) => {
    const alert = form.querySelector('[data-contact-alert]');
    if (!alert) return;
    alert.textContent = message;
    alert.hidden = false;
    alert.focus?.({ preventScroll: true });
  };

  const hideAlert = (form) => {
    const alert = form.querySelector('[data-contact-alert]');
    if (alert) {
      alert.hidden = true;
      alert.textContent = '';
    }
  };

  forms.forEach((form, index) => {
    const startedAt = Date.now();
    const startedField = form.querySelector('[data-contact-started]');
    const timer = form.querySelector('[data-contact-timer]');
    const submitButton = form.querySelector('[data-contact-submit]');
    const honeypot = form.querySelector('[data-contact-honeypot]');
    const messageField = form.querySelector('textarea[name="contact[body]"]');
    const subjectField = form.querySelector('input[name="contact[Subject]"]');
    const formKey = `vascoraContactLastSubmit:${index}`;

    if (startedField) startedField.value = new Date(startedAt).toISOString();

    const syncTimer = () => {
      if (!timer) return;
      const elapsed = (Date.now() - startedAt) / 1000;
      if (elapsed >= MIN_SECONDS) {
        timer.textContent = 'Security check ready';
      } else {
        timer.textContent = `Security check ${Math.ceil(MIN_SECONDS - elapsed)}s`;
      }
    };
    syncTimer();
    const interval = window.setInterval(() => {
      syncTimer();
      if ((Date.now() - startedAt) / 1000 >= MIN_SECONDS) window.clearInterval(interval);
    }, 300);

    form.addEventListener('input', () => hideAlert(form));

    form.addEventListener('submit', (event) => {
      hideAlert(form);
      form.classList.add('is-checking');

      const elapsed = (Date.now() - startedAt) / 1000;
      const body = `${subjectField?.value || ''} ${messageField?.value || ''}`.toLowerCase();
      const links = body.match(urlPattern) || [];
      const signalCount = spamSignals.reduce((count, signal) => count + (body.includes(signal) ? 1 : 0), 0);
      const lastSubmit = Number(window.localStorage.getItem(formKey) || 0);
      const secondsSinceLastSubmit = (Date.now() - lastSubmit) / 1000;

      const block = (message) => {
        event.preventDefault();
        form.classList.remove('is-checking');
        showAlert(form, message);
        if (submitButton) submitButton.disabled = false;
      };

      if (honeypot && honeypot.value.trim() !== '') {
        block('Your message could not be submitted. Please refresh the page and try again.');
        return;
      }

      if (elapsed < MIN_SECONDS) {
        block('Please review your message for a moment before submitting. This protects Vascora Group from automated spam.');
        return;
      }

      if (messageField && messageField.value.trim().length < 10) {
        block('Please add a little more detail to your message before submitting.');
        return;
      }

      if (links.length > 4 || signalCount >= 2) {
        block('This message looks like automated or promotional spam. Please remove excessive links or sales outreach language and try again.');
        return;
      }

      if (lastSubmit && secondsSinceLastSubmit < RATE_LIMIT_SECONDS) {
        block('Please wait a few seconds before sending another message.');
        return;
      }

      window.localStorage.setItem(formKey, String(Date.now()));
      form.classList.remove('is-checking');
      form.classList.add('is-submitting');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }
    });
  });
})();
